import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import UsersTableRow from './UsersTableRow';

const TABLE_COLUMNS = [
  { id: 'avatar', name: '', sortable: false, className: 'w-[50px]' },
  { id: 'email', name: 'User', sortable: false, className: 'min-w-[200px]' },
  { id: 'level', name: 'Current Level', sortable: true, className: 'text-center' },
  { id: 'progress', name: 'Progress', sortable: true, className: 'text-center min-w-[150px]' },
  { id: 'goal', name: 'Goal', sortable: true, className: 'text-center' },
  { id: 'englishLevel', name: 'English', sortable: true, className: 'text-center' },
  { id: 'lastLoginAt', name: 'Last Login', sortable: true, className: 'text-center' },
  { id: 'actions', name: '', sortable: false, className: 'w-[80px]' },
];

function UsersTable({
  onArchiveUser,
  onUnArchiveUser,
  setIsLoading,
  isLoading,
  usersData,
  growLevels,
  setSort,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalCount,
}) {
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('level');

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  useEffect(() => {
    if (orderBy) {
      setSort(order === 'asc' ? orderBy : `-${orderBy}`);
      setIsLoading(true);
    }
  }, [orderBy, order]);

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
      setIsLoading(true);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
      setIsLoading(true);
    }
  };

  return (
    <Card style={{ border: '1px solid hsl(var(--border))' }}>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <TableHead key={column.id} className={column.className}>
                    {column.sortable ? (
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:text-foreground transition-colors"
                        onClick={() => handleSort(column.id)}
                      >
                        {column.name}
                        <ArrowUpDown className="h-3 w-3" />
                      </button>
                    ) : (
                      column.name
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 5 }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={index}>
                    <TableCell>
                      <div className="h-10 w-10 bg-muted animate-pulse rounded-full" />
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                        <div className="h-3 w-48 bg-muted animate-pulse rounded" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="h-6 w-16 bg-muted animate-pulse rounded-full mx-auto" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-24 bg-muted animate-pulse rounded mx-auto" />
                    </TableCell>
                    <TableCell>
                      <div className="h-6 w-16 bg-muted animate-pulse rounded-full mx-auto" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-12 bg-muted animate-pulse rounded mx-auto" />
                    </TableCell>
                    <TableCell>
                      <div className="h-4 w-20 bg-muted animate-pulse rounded mx-auto" />
                    </TableCell>
                    <TableCell>
                      <div className="h-8 w-8 bg-muted animate-pulse rounded mx-auto" />
                    </TableCell>
                  </TableRow>
                ))}
              {!isLoading && usersData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No users found
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                usersData.length > 0 &&
                usersData.map((user) => (
                  <UsersTableRow
                    key={user._id}
                    user={user}
                    onUnArchive={onUnArchiveUser}
                    onArchive={onArchiveUser}
                    growLevels={growLevels}
                    onSave={() => setIsLoading(true)}
                  />
                ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                  setIsLoading(true);
                }}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, totalCount)} of{' '}
                {totalCount}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handlePrevPage}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleNextPage}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

UsersTable.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  onArchiveUser: PropTypes.func.isRequired,
  onUnArchiveUser: PropTypes.func.isRequired,
  growLevels: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      _id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  setSort: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  setRowsPerPage: PropTypes.func.isRequired,
  totalCount: PropTypes.number.isRequired,
  usersData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default UsersTable;
